import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import useScheduledCalls from '../../hooks/useScheduledCalls';

// Tow-truck icon — Matt's custom SVG asset served from /public/icons.
// Sized to match the emoji glyphs used by the other bucket cards.
function TowTruckIcon({ size = 36 }) {
  return (
    <img
      src={`${process.env.PUBLIC_URL}/icons/tow-truck.svg`}
      alt="Tow truck"
      width={size}
      height={size}
      style={{ display: 'block', objectFit: 'contain' }}
    />
  );
}

/**
 * Dispatch Hub — landing page for the Dispatch module.
 *
 * Mirrors the Sales Requests overview style: a grid of bucket cards
 * with live counts where they make sense. Each card is a button that
 * navigates to the existing dispatch sub-page; this hub is purely a
 * launcher, not a new data source.
 *
 * Visibility:
 *   - Driver Configuration card is super_admin only.
 *   - Setup card hidden unless the user can configure dispatch.
 *   - Driver Communications hidden for roles without access.
 */

export default function DispatchHubPage() {
  const navigate = useNavigate();
  const { user, can, canInModule, isSuperAdmin, isConfigurator, isAtLeastManager, isDispatcher, isDriver } = useAuth();
  const { scheduledCount } = useScheduledCalls();
  const [queueCount, setQueueCount] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.getPublicStats()
      .then(data => { if (mounted && data) setQueueCount(data.total_calls ?? 0); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const canSeeDriverChat = !!(user && (user.is_station_account || isDispatcher || isAtLeastManager || isDriver));

  const buckets = [
    {
      key: 'queue',
      label: 'Space Call Queue',
      icon: 'tow_truck',
      color: '#2563eb',
      to: '/queue',
      count: queueCount,
      description: 'Open + claimed service calls',
      show: true,
    },
    {
      key: 'scheduled',
      label: 'Scheduled Calls',
      icon: '📅',
      color: '#6d28d9',
      to: '/scheduled',
      count: scheduledCount ?? 0,
      description: 'Calls queued ahead of their due time',
      show: true,
    },
    {
      key: 'driver_comms',
      label: 'Driver Communications',
      icon: '💬',
      color: '#16a34a',
      to: '/driver-chat',
      count: null,
      description: 'Driver chat threads and broadcasts',
      show: canSeeDriverChat,
    },
    {
      // Private escrow ledger. Admin-only until the driver-facing rollout —
      // drivers must not see this exists. Dispatchers/sales don't either.
      key: 'damage_fund',
      label: 'Damage Fund',
      icon: '🛡️',
      color: '#0f766e',
      to: '/damage-fund',
      count: null,
      description: 'Driver escrow balances and ledger',
      show: isSuperAdmin || can('view_all_damage_funds') || can('manage_damage_fund'),
    },
    {
      key: 'setup',
      label: 'Setup',
      icon: '⚙️',
      color: '#475569',
      to: '/dispatch/setup',
      count: null,
      description: 'Call types, reasons, VSFs, properties',
      show: isConfigurator('dispatch'),
    },
    {
      key: 'driver_config',
      label: 'Driver Configuration',
      icon: '🛠️',
      color: '#c9a227',
      to: '/admin/drivers',
      count: null,
      description: 'Driver profiles, shifts, regions',
      show: isSuperAdmin,
    },
  ].filter(b => b.show);

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Dispatch</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Pick a bucket to jump into. Counts reflect current company-wide volume.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16,
      }}>
        {buckets.map(b => (
          <button
            key={b.key}
            onClick={() => navigate(b.to)}
            style={{
              textAlign: 'left',
              padding: 20,
              borderRadius: 12,
              border: `1px solid ${b.color}30`,
              background: `linear-gradient(135deg, ${b.color}08 0%, transparent 100%)`,
              cursor: 'pointer',
              transition: 'transform 0.1s ease, box-shadow 0.1s ease',
              minHeight: 140,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 4px 16px ${b.color}25`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {b.icon === 'tow_truck' ? (
                <TowTruckIcon size={36} />
              ) : (
                <span style={{ fontSize: 28, lineHeight: 1 }}>{b.icon}</span>
              )}
              <span style={{ fontWeight: 700, fontSize: 15, color: b.color }}>
                {b.label}
              </span>
            </div>
            <div style={{
              fontSize: 48, fontWeight: 800, lineHeight: 1,
              color: b.count === null ? '#cbd5e1' : 'var(--text-primary)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {b.count === null ? '→' : b.count}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
              {b.count === null ? 'Open' : 'Right now'}
            </div>
            {b.description && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.4 }}>
                {b.description}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
